import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
    CheckCircle2, AlertCircle, Loader2, Mail, Phone, User as UserIcon, Lock, Briefcase,
    MapPin, Heart, Building, Upload, FileText, Check, ChevronRight, ChevronLeft,
    PartyPopper, Sparkles,
} from "lucide-react";
import API_URL from "../utils/api";
import logger from "../utils/logger";
import { toast } from "sonner";

const STEPS = [
    { id: "account", label: "Account", icon: UserIcon },
    { id: "profile", label: "Personal Details", icon: Heart },
    { id: "documents", label: "KYC Documents", icon: FileText },
    { id: "done", label: "Welcome", icon: PartyPopper },
];

const DOC_TYPES = [
    { value: "aadhar", label: "Aadhaar Card", required: true },
    { value: "pan", label: "PAN Card", required: true },
    { value: "address_proof", label: "Address Proof", required: false },
    { value: "bank_proof", label: "Bank Passbook / Statement", required: false },
    { value: "photo", label: "Passport Photo", required: false },
    { value: "education_degree", label: "Highest Degree", required: false },
    { value: "experience_letter", label: "Experience Letter", required: false },
    { value: "resume", label: "Resume / CV", required: false },
    { value: "other", label: "Other", required: false },
];

const OnboardingJoin = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, checkAuth } = useAuth();
    const fileRef = useRef(null);

    const [loadingToken, setLoadingToken] = useState(true);
    const [tokenError, setTokenError] = useState("");
    const [invite, setInvite] = useState(null);

    const [step, setStep] = useState(0);
    const [registeredUser, setRegisteredUser] = useState(null);
    const [bearerToken, setBearerToken] = useState(null); // Fallback for cookie failures on prod CDNs

    // Step 0: Account
    const [account, setAccount] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });

    // Step 1: Personal Details
    const [profile, setProfile] = useState({
        date_of_birth: "", gender: "", address: "", city: "", state: "", blood_group: "",
        emergency_contact_name: "", emergency_contact_phone: "",
        bank_account_number: "", bank_name: "", bank_ifsc: "",
    });

    // Step 2: Documents
    const [documents, setDocuments] = useState([]);
    const [docType, setDocType] = useState("aadhar");
    const [uploading, setUploading] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");

    const validateInvite = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/public/onboarding-invites/${token}`);
            setInvite(data);
            setAccount((f) => ({ ...f, email: data.email || "", name: data.name_hint || "" }));
        } catch (e) {
            const msg = e?.response?.data?.detail || "Invalid or expired link";
            setTokenError(typeof msg === "string" ? msg : "Invalid or expired link");
        } finally {
            setLoadingToken(false);
        }
    }, [token]);

    useEffect(() => { validateInvite(); }, [validateInvite]);

    // Build axios config — always include cookies AND fall back to Bearer token (so
    // the wizard works even if SameSite=None cookies get dropped by mobile/CDN proxies).
    const authedConfig = useCallback((extra = {}) => ({
        withCredentials: true,
        ...extra,
        headers: {
            ...(extra.headers || {}),
            ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        },
    }), [bearerToken]);

    const refreshDocuments = useCallback(async (userId) => {
        try {
            const { data } = await axios.get(`${API_URL}/api/employees/${userId}/documents`, authedConfig());
            setDocuments(data);
        } catch (e) { logger.error("Fetch docs failed", e); }
    }, [authedConfig]);

    // ============== STEP HANDLERS ==============
    const handleAccountSubmit = async (e) => {
        e.preventDefault();
        setSubmitError("");
        if (account.password.length < 6) { setSubmitError("Password must be at least 6 characters"); return; }
        if (account.password !== account.confirm) { setSubmitError("Passwords do not match"); return; }
        // Reject obvious PAN/Aadhaar patterns in the Name field — that's a frequent typo
        const trimmedName = account.name.trim();
        if (/^[A-Z]{5}\d{4}[A-Z]$/i.test(trimmedName)) {
            setSubmitError("That looks like your PAN, not your full name. Please enter your full name (e.g. \"Subhayan Roy\").");
            return;
        }
        if (/^\d{10,16}$/.test(trimmedName.replace(/\s/g, ""))) {
            setSubmitError("That looks like a phone or Aadhaar number, not your name. Please enter your full name.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/public/onboarding-invites/${token}/register`,
                { name: account.name.trim(), email: account.email.trim().toLowerCase(), phone: account.phone.trim(), password: account.password },
                { withCredentials: true },
            );
            setRegisteredUser(data);
            // Capture the bearer token (fallback for cookie failures on mobile/CDN)
            if (data?.access_token) setBearerToken(data.access_token);
            await checkAuth();
            // Pre-fill phone into profile step
            setProfile((p) => ({ ...p, phone: account.phone }));
            // If they already finished onboarding earlier, send them straight to the dashboard
            if (data?.onboarding_completed) {
                toast.success("Welcome back! You've already completed onboarding.");
                navigate("/dashboard", { replace: true });
                return;
            }
            setStep(1);
            if (data?.resumed) {
                toast.success("Welcome back — picking up where you left off.");
            } else {
                toast.success("Account created! Now tell us a bit about yourself.");
            }
        } catch (err) {
            logger.error("Step 1 register failed", err);
            const msg = err?.response?.data?.detail || "Registration failed. Please try again.";
            setSubmitError(typeof msg === "string" ? msg : "Registration failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setSubmitError("");
        setSubmitting(true);
        try {
            await axios.put(`${API_URL}/api/self-onboarding/profile`, profile, authedConfig());
            // Pre-load documents (will be empty initially)
            if (registeredUser?._id) await refreshDocuments(registeredUser._id);
            setStep(2);
            toast.success("Profile saved! Now upload your documents.");
        } catch (err) {
            logger.error("Profile save failed", err);
            const msg = err?.response?.data?.detail || "Could not save your profile.";
            setSubmitError(typeof msg === "string" ? msg : "Could not save your profile.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10 MB)"); return; }
        if (!registeredUser?._id) { toast.error("Session lost. Please refresh and start again."); return; }
        setUploading(true);
        const formData = new FormData();
        formData.append("file", file);
        try {
            await axios.post(
                `${API_URL}/api/employees/${registeredUser._id}/documents?doc_type=${docType}`,
                formData,
                authedConfig({ headers: { "Content-Type": "multipart/form-data" } }),
            );
            await refreshDocuments(registeredUser._id);
            toast.success(`${DOC_TYPES.find((t) => t.value === docType)?.label || "Document"} uploaded`);
        } catch (err) {
            logger.error("Doc upload failed", err);
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const handleFinish = async () => {
        setSubmitting(true);
        try {
            await axios.post(`${API_URL}/api/self-onboarding/complete`, {}, authedConfig());
            await checkAuth();
            setStep(3);
        } catch (err) {
            logger.error("Complete failed", err);
            toast.error("Could not finalize onboarding. You can complete it later from the dashboard.");
            setStep(3);
        } finally {
            setSubmitting(false);
        }
    };

    // ============== RENDER GUARDS ==============
    if (isAuthenticated && step === 0 && !registeredUser) {
        return <Navigate to="/dashboard" replace />;
    }

    if (loadingToken) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-blue-100 flex items-center justify-center px-4">
                <div className="flex items-center gap-3 text-slate-600"><Loader2 className="h-5 w-5 animate-spin" /> Verifying your invite link...</div>
            </div>
        );
    }

    if (tokenError) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-blue-100 flex items-center justify-center px-4">
                <Card className="max-w-md w-full rounded-2xl shadow-xl border-0">
                    <CardContent className="p-8 text-center">
                        <div className="w-16 h-16 mx-auto rounded-full bg-rose-100 flex items-center justify-center mb-4"><AlertCircle className="h-8 w-8 text-rose-600" /></div>
                        <h1 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>Link Unavailable</h1>
                        <p className="text-slate-600 mb-6" data-testid="invite-error-message">{tokenError}</p>
                        <p className="text-sm text-slate-500 mb-4">Please contact your hiring manager for a fresh invite link.</p>
                        <Button onClick={() => navigate("/login")} className="bg-brand hover:bg-brand text-white" data-testid="invite-error-login-btn">Go to Login</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-blue-100 px-4 py-8">
            <div className="max-w-3xl mx-auto">
                {/* Logo + Header */}
                <div className="text-center mb-6">
                    <img src="/digi-loans-logo.png" alt="DIGI-LOANS" className="h-16 w-16 mx-auto mb-3 rounded-2xl shadow-md" />
                    <h1 className="text-3xl font-black text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Welcome to DIGI-LOANS</h1>
                    <p className="text-slate-500 mt-1 text-sm">Loan Simplified · Self-Onboarding in 4 quick steps</p>
                </div>

                {/* Stepper */}
                <Card className="rounded-2xl shadow-xl border-0 overflow-hidden mb-1" data-testid="invite-wizard-card">
                    <div className="bg-gradient-to-r from-brand to-blue-700 text-white px-6 py-4">
                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest opacity-90">
                            <Briefcase className="h-3 w-3" /> Joining as
                        </div>
                        <div className="flex items-center justify-between flex-wrap gap-2 mt-0.5">
                            <div className="text-lg font-black" style={{ fontFamily: "Chivo, sans-serif" }} data-testid="invite-role-label">{invite?.role_label || "Employee"}</div>
                            <Badge className="bg-white/20 text-white border-0 rounded-full text-[10px]">
                                {[invite?.department, invite?.designation].filter(Boolean).join(" · ") || "DIGI-LOANS Team"}
                            </Badge>
                        </div>
                    </div>

                    {/* Stepper Bar */}
                    <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
                        <div className="flex items-center gap-1">
                            {STEPS.map((s, i) => {
                                const Icon = s.icon;
                                const done = i < step;
                                const active = i === step;
                                return (
                                    <div key={s.id} className="flex items-center flex-1">
                                        <div className={`flex items-center gap-2 ${active ? "text-brand" : done ? "text-emerald-600" : "text-slate-400"}`}>
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                                                done ? "bg-emerald-500 border-emerald-500 text-white" : active ? "bg-brand border-brand text-white" : "bg-white border-slate-300"
                                            }`} data-testid={`stepper-${s.id}`}>
                                                {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                                            </div>
                                            <span className="text-[11px] font-bold uppercase tracking-wider hidden sm:block">{s.label}</span>
                                        </div>
                                        {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${done ? "bg-emerald-500" : "bg-slate-200"}`} />}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <CardContent className="p-6 sm:p-8">
                        {/* STEP 0: ACCOUNT */}
                        {step === 0 && (
                            <form onSubmit={handleAccountSubmit} className="space-y-5" data-testid="step-account">
                                <div>
                                    <h2 className="text-lg font-black text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Step 1 of 4 — Create your account</h2>
                                    <p className="text-xs text-slate-500 mt-0.5">Set up your DIGI-LOANS login credentials.</p>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><UserIcon className="h-3 w-3" /> Full Name</Label>
                                        <Input value={account.name} onChange={(e) => setAccount({ ...account, name: e.target.value })} placeholder="As per Aadhaar / PAN" required minLength={2} className="rounded-lg bg-slate-50 border-slate-200 mt-1" data-testid="invite-name-input" />
                                    </div>
                                    <div>
                                        <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Mail className="h-3 w-3" /> Email</Label>
                                        <Input type="email" value={account.email} onChange={(e) => setAccount({ ...account, email: e.target.value })} placeholder="you@example.com" required disabled={!!invite?.email} className="rounded-lg bg-slate-50 border-slate-200 mt-1 disabled:opacity-70" data-testid="invite-email-input" />
                                        {invite?.email && <p className="text-[10px] text-slate-400 mt-1">Pre-filled by HR</p>}
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Phone className="h-3 w-3" /> Mobile Number</Label>
                                    <Input type="tel" value={account.phone} onChange={(e) => setAccount({ ...account, phone: e.target.value })} placeholder="+91 98XXX XXXXX" required minLength={8} className="rounded-lg bg-slate-50 border-slate-200 mt-1" data-testid="invite-phone-input" />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Lock className="h-3 w-3" /> Password</Label>
                                        <Input type="password" value={account.password} onChange={(e) => setAccount({ ...account, password: e.target.value })} placeholder="Min 6 characters" required minLength={6} className="rounded-lg bg-slate-50 border-slate-200 mt-1" data-testid="invite-password-input" />
                                    </div>
                                    <div>
                                        <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Lock className="h-3 w-3" /> Confirm Password</Label>
                                        <Input type="password" value={account.confirm} onChange={(e) => setAccount({ ...account, confirm: e.target.value })} placeholder="Re-type" required minLength={6} className="rounded-lg bg-slate-50 border-slate-200 mt-1" data-testid="invite-confirm-input" />
                                    </div>
                                </div>
                                {submitError && (
                                    <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2" data-testid="invite-submit-error">
                                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" /><span>{submitError}</span>
                                    </div>
                                )}
                                <Button type="submit" disabled={submitting} className="w-full bg-brand hover:bg-brand text-white rounded-lg h-11 text-sm font-bold" data-testid="invite-submit-btn">
                                    {submitting ? (<span className="flex items-center gap-2 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Creating account...</span>) :
                                                 (<span className="flex items-center gap-2 justify-center">Continue to Personal Details <ChevronRight className="h-4 w-4" /></span>)}
                                </Button>
                            </form>
                        )}

                        {/* STEP 1: PROFILE */}
                        {step === 1 && (
                            <form onSubmit={handleProfileSubmit} className="space-y-5" data-testid="step-profile">
                                <div>
                                    <h2 className="text-lg font-black text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Step 2 of 4 — Personal Details</h2>
                                    <p className="text-xs text-slate-500 mt-0.5">A few more details so HR has everything ready for you.</p>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Date of Birth</Label>
                                        <Input type="date" value={profile.date_of_birth} onChange={(e) => setProfile({ ...profile, date_of_birth: e.target.value })} required className="rounded-lg bg-slate-50 border-slate-200 mt-1" data-testid="profile-dob" />
                                    </div>
                                    <div>
                                        <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Gender</Label>
                                        <Select value={profile.gender || "not_set"} onValueChange={(v) => setProfile({ ...profile, gender: v === "not_set" ? "" : v })}>
                                            <SelectTrigger className="rounded-lg bg-slate-50 border-slate-200 mt-1" data-testid="profile-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="not_set">Select Gender</SelectItem>
                                                <SelectItem value="male">Male</SelectItem>
                                                <SelectItem value="female">Female</SelectItem>
                                                <SelectItem value="other">Other</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><MapPin className="h-3 w-3" /> Address</Label>
                                    <Input value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} placeholder="Street, area" className="rounded-lg bg-slate-50 border-slate-200 mt-1" data-testid="profile-address" />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div><Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">City</Label>
                                        <Input value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} className="rounded-lg bg-slate-50 border-slate-200 mt-1" /></div>
                                    <div><Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">State</Label>
                                        <Input value={profile.state} onChange={(e) => setProfile({ ...profile, state: e.target.value })} className="rounded-lg bg-slate-50 border-slate-200 mt-1" /></div>
                                    <div>
                                        <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Blood Group</Label>
                                        <Select value={profile.blood_group || "not_set"} onValueChange={(v) => setProfile({ ...profile, blood_group: v === "not_set" ? "" : v })}>
                                            <SelectTrigger className="rounded-lg bg-slate-50 border-slate-200 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="not_set">—</SelectItem>
                                                {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((bg) => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="pt-3 border-t border-slate-100">
                                    <h3 className="text-xs font-black text-slate-700 mb-3 uppercase tracking-wider flex items-center gap-1.5"><Heart className="h-3 w-3 text-rose-500" /> Emergency Contact</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div><Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Name</Label>
                                            <Input value={profile.emergency_contact_name} onChange={(e) => setProfile({ ...profile, emergency_contact_name: e.target.value })} className="rounded-lg bg-slate-50 border-slate-200 mt-1" data-testid="profile-emergency-name" /></div>
                                        <div><Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Phone</Label>
                                            <Input value={profile.emergency_contact_phone} onChange={(e) => setProfile({ ...profile, emergency_contact_phone: e.target.value })} className="rounded-lg bg-slate-50 border-slate-200 mt-1" data-testid="profile-emergency-phone" /></div>
                                    </div>
                                </div>

                                <div className="pt-3 border-t border-slate-100">
                                    <h3 className="text-xs font-black text-slate-700 mb-3 uppercase tracking-wider flex items-center gap-1.5"><Building className="h-3 w-3 text-blue-500" /> Bank Details (for Salary)</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <div><Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Bank Name</Label>
                                            <Input value={profile.bank_name} onChange={(e) => setProfile({ ...profile, bank_name: e.target.value })} className="rounded-lg bg-slate-50 border-slate-200 mt-1" data-testid="profile-bank-name" /></div>
                                        <div><Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Account Number</Label>
                                            <Input value={profile.bank_account_number} onChange={(e) => setProfile({ ...profile, bank_account_number: e.target.value })} className="rounded-lg bg-slate-50 border-slate-200 mt-1" /></div>
                                        <div><Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">IFSC Code</Label>
                                            <Input value={profile.bank_ifsc} onChange={(e) => setProfile({ ...profile, bank_ifsc: e.target.value })} className="rounded-lg bg-slate-50 border-slate-200 mt-1" /></div>
                                    </div>
                                </div>

                                {submitError && (
                                    <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" /><span>{submitError}</span>
                                    </div>
                                )}

                                <div className="flex justify-between gap-3 pt-2">
                                    <span className="text-[11px] text-slate-400 self-center">Step 2 of 4</span>
                                    <Button type="submit" disabled={submitting} className="bg-brand hover:bg-brand text-white rounded-lg" data-testid="profile-submit-btn">
                                        {submitting ? (<span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Saving...</span>) :
                                                     (<span className="flex items-center gap-2">Continue to Documents <ChevronRight className="h-4 w-4" /></span>)}
                                    </Button>
                                </div>
                            </form>
                        )}

                        {/* STEP 2: DOCUMENTS */}
                        {step === 2 && (
                            <div className="space-y-5" data-testid="step-documents">
                                <div>
                                    <h2 className="text-lg font-black text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Step 3 of 4 — Upload KYC Documents</h2>
                                    <p className="text-xs text-slate-500 mt-0.5">Upload at least your <strong>Aadhaar</strong> and <strong>PAN</strong>. HR will verify within 24 hours.</p>
                                </div>

                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center gap-3">
                                    <div className="flex-1">
                                        <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Document Type</Label>
                                        <Select value={docType} onValueChange={setDocType}>
                                            <SelectTrigger className="rounded-lg bg-white border-slate-200" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}{t.required ? " *" : ""}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-brand text-white rounded-lg mt-5" data-testid="upload-btn">
                                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" />Choose File</>}
                                    </Button>
                                    <input ref={fileRef} type="file" className="hidden" onChange={handleFileUpload} accept=".pdf,.jpg,.jpeg,.png" />
                                </div>

                                {documents.length > 0 ? (
                                    <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 bg-white">
                                        {documents.map((doc) => (
                                            <div key={doc._id} className="flex items-center gap-3 px-4 py-3" data-testid={`uploaded-doc-${doc._id}`}>
                                                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center"><FileText className="h-4 w-4 text-emerald-600" /></div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-slate-900 truncate">{doc.original_filename}</p>
                                                    <p className="text-[10px] text-slate-400">{DOC_TYPES.find((t) => t.value === doc.doc_type)?.label || doc.doc_type}</p>
                                                </div>
                                                <Badge className="rounded-full bg-emerald-100 text-emerald-700 text-[10px]"><Check className="h-3 w-3 inline mr-0.5" />Uploaded</Badge>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="border border-dashed border-slate-300 rounded-xl py-10 text-center bg-slate-50">
                                        <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                                        <p className="text-sm text-slate-500">No documents uploaded yet</p>
                                        <p className="text-xs text-slate-400 mt-1">Upload Aadhaar and PAN to continue</p>
                                    </div>
                                )}

                                <div className="flex justify-between gap-3 pt-2">
                                    <Button variant="outline" onClick={() => setStep(1)} className="rounded-lg" data-testid="back-to-profile-btn">
                                        <ChevronLeft className="h-4 w-4 mr-1" /> Back
                                    </Button>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px] text-slate-400 self-center">Step 3 of 4</span>
                                        <Button onClick={handleFinish} disabled={submitting || documents.length === 0} className="bg-brand hover:bg-brand text-white rounded-lg" data-testid="finish-btn">
                                            {submitting ? (<span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Finalizing...</span>) :
                                                         (<span className="flex items-center gap-2">Finish Onboarding <ChevronRight className="h-4 w-4" /></span>)}
                                        </Button>
                                    </div>
                                </div>
                                {documents.length === 0 && (
                                    <p className="text-[11px] text-center text-slate-400">Upload at least one document to continue.</p>
                                )}
                            </div>
                        )}

                        {/* STEP 3: DONE */}
                        {step === 3 && (
                            <div className="text-center py-6" data-testid="step-done">
                                <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mb-5 shadow-lg">
                                    <PartyPopper className="h-10 w-10 text-white" />
                                </div>
                                <h2 className="text-3xl font-black text-slate-900 mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>You're In! 🎉</h2>
                                <p className="text-slate-600 mb-6 max-w-md mx-auto">
                                    Welcome aboard, <strong>{registeredUser?.name || "team"}</strong>! Your DIGI-LOANS account is ready and your documents are with HR for verification (typically same-day).
                                </p>
                                <div className="flex flex-wrap justify-center gap-2 mb-7">
                                    <Badge className="rounded-full bg-emerald-100 text-emerald-700 px-3 py-1"><Check className="h-3 w-3 mr-1 inline" />Account Created</Badge>
                                    <Badge className="rounded-full bg-emerald-100 text-emerald-700 px-3 py-1"><Check className="h-3 w-3 mr-1 inline" />Profile Complete</Badge>
                                    <Badge className="rounded-full bg-emerald-100 text-emerald-700 px-3 py-1"><Check className="h-3 w-3 mr-1 inline" />{documents.length} Document{documents.length === 1 ? "" : "s"} Uploaded</Badge>
                                </div>
                                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6 text-left text-xs text-slate-600 max-w-md mx-auto">
                                    <p className="font-bold text-slate-700 mb-1.5 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-blue-500" /> What's next?</p>
                                    <ul className="space-y-1 ml-1">
                                        <li>• HR verifies your Aadhaar &amp; PAN within 24 hours</li>
                                        <li>• Your reporting manager will reach out to schedule a 1-on-1</li>
                                        <li>• Meanwhile, explore the dashboard, attendance, and CRM tools</li>
                                    </ul>
                                </div>
                                <Button onClick={() => navigate("/dashboard")} className="bg-brand hover:bg-brand text-white rounded-lg px-10 py-3 text-sm font-bold" data-testid="go-to-dashboard-btn">
                                    Go to Dashboard <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {step < 3 && (
                    <p className="text-center text-[11px] text-slate-400 mt-4">
                        Already have an account?{" "}
                        <button onClick={() => navigate("/login")} className="text-brand font-semibold hover:underline" data-testid="invite-login-link">Log in</button>
                    </p>
                )}
            </div>
        </div>
    );
};

export default OnboardingJoin;
